<template>
  <div id="app">
    <router-view v-if="!hasError" />
    <div v-else class="error-boundary">
      <h3>Application encountered an error</h3>
      <p>{{ errorMessage }}</p>
      <button @click="reloadApp" class="reload-btn">Reload</button>
    </div>
    <ChatWidget />
  </div>
</template>

<script>
import { ref, onErrorCaptured, onMounted } from 'vue'
import { initWechatEnvironment } from '@/utils/wechatEnvironment'
import store from '@/store'
import ChatWidget from '@/components/ChatWidget.vue'

export default {
  name: 'AppMain',
  components: {
    ChatWidget
  },
  setup() {
    const hasError = ref(false)
    const errorMessage = ref('')
    
    // Error boundary
    onErrorCaptured((error, instance, info) => {
      console.error('App Error Captured:', error, info)
      hasError.value = true
      errorMessage.value = error.message || 'Unknown error'
      return false // Prevent error from propagating
    })
    
    const reloadApp = () => {
      hasError.value = false
      errorMessage.value = ''
      window.location.reload()
    }
    
    onMounted(() => {
      try {
        // Use unified WeChat environment initialization
        const envInfo = initWechatEnvironment()
        console.log('WeChat environment info:', envInfo)
        
        // Initialize user state from localStorage
        store.dispatch('initUser')
        
        // Initialize Baidu Analytics
        var _hmt = _hmt || [];
        (function() {
          var hm = document.createElement("script");
          hm.src = "https://hm.baidu.com/hm.js?1b185a5d852fe81a534de69d95d483d4";
          var s = document.getElementsByTagName("script")[0]; 
          s.parentNode.insertBefore(hm, s);
        })();
        
        // 强制调整Tawk.to位置 - 立即开始监控
        startTawktoPositionFix()
      } catch (error) {
        console.error('WeChat initialization error:', error)
      }
    })
    
    // 强制修复Tawk.to位置的函数
    const startTawktoPositionFix = () => {
      console.log('开始强制修复Tawk.to位置')
      
      const forceFixPosition = () => {
        console.log('执行强制位置修复...')
        
        // 检查Tawk.to是否已加载
        if (window.Tawk_API) {
          console.log('Tawk_API已加载，可用方法:', Object.keys(window.Tawk_API))
        } else {
          console.log('Tawk_API尚未加载')
        }
        
        const selectors = [
          '#tawkchat-minified-container',
          '#tawkchat-container', 
          '[id*="tawk"]',
          '[class*="tawk"]',
          'div[style*="position: fixed"]',
          'div[style*="position:fixed"]',
          'iframe[src*="tawk"]',
          'div[style*="bottom: 20px"]',
          'div[style*="bottom:20px"]',
          'div[style*="bottom: 10px"]',
          'div[style*="bottom:10px"]'
        ]
        
        let adjustedCount = 0
        
        selectors.forEach(selector => {
          const elements = document.querySelectorAll(selector)
          if (elements.length > 0) {
            console.log(`找到 ${elements.length} 个元素匹配选择器: ${selector}`)
          }
          
          elements.forEach(element => {
            if (element && element.style) {
              const oldBottom = element.style.bottom
              const oldZIndex = element.style.zIndex
              
              element.style.setProperty('bottom', '90px', 'important')
              element.style.setProperty('z-index', '999', 'important')
              
              console.log(`调整元素位置: ${element.tagName}#${element.id}.${element.className}`)
              console.log(`  底部位置: ${oldBottom} -> 90px`)
              console.log(`  层级: ${oldZIndex} -> 999`)
              
              adjustedCount++
            }
          })
        })
        
        if (adjustedCount > 0) {
          console.log(`本次调整了 ${adjustedCount} 个元素`)
        } else {
          console.log('未找到需要调整的Tawk.to元素')
        }
        
        // 检查所有固定定位的元素
        const allFixed = document.querySelectorAll('*')
        let foundTawkElements = 0
        allFixed.forEach(el => {
          const style = window.getComputedStyle(el)
          if (style.position === 'fixed' && (
            el.id.includes('tawk') || 
            el.className.includes('tawk') ||
            (el.tagName === 'IFRAME' && el.src && el.src.includes('tawk'))
          )) {
            console.log('发现Tawk.to固定定位元素:', el)
            foundTawkElements++
          }
        })
        
        if (foundTawkElements === 0) {
          console.log('未发现任何Tawk.to相关的固定定位元素')
        }
      }
      
      // 立即执行一次
      forceFixPosition()
      
      // 定时执行
      setTimeout(forceFixPosition, 500)
      setTimeout(forceFixPosition, 1000)
      setTimeout(forceFixPosition, 2000)
      setTimeout(forceFixPosition, 3000)
      setTimeout(forceFixPosition, 5000)
      setTimeout(forceFixPosition, 10000)
      
      // 持续监控
      const intervalId = setInterval(forceFixPosition, 3000)
      
      // DOM变化监控
      const observer = new MutationObserver((mutations) => {
        let shouldCheck = false
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              const element = node
              if (element.id && element.id.includes('tawk') ||
                  element.className && element.className.includes('tawk') ||
                  (element.tagName === 'IFRAME' && element.src && element.src.includes('tawk'))) {
                shouldCheck = true
              }
            }
          })
        })
        
        if (shouldCheck) {
          console.log('检测到Tawk.to相关DOM变化，执行位置调整')
          setTimeout(forceFixPosition, 100)
        }
      })
      observer.observe(document.body, { childList: true, subtree: true })
      
      // 暴露到全局，方便调试
      window.forceFixTawktoPosition = forceFixPosition
      window.stopTawktoMonitor = () => {
        clearInterval(intervalId)
        observer.disconnect()
        console.log('已停止Tawk.to位置监控')
      }
    }
    
    return {
      hasError,
      errorMessage,
      reloadApp
    }
  }
}
</script>

<style>
html, body {
  margin: 0;
  padding: 0;
  background-color: #f7f8fa;
  overflow-x: hidden;
  height: 100%;
}

body {
  overflow-y: auto;
}

#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica,
    Segoe UI, Arial, Roboto, 'PingFang SC', 'miui', 'Hiragino Sans GB', 'Microsoft Yahei',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  width: 100%;
  min-height: 100%;
  overflow: visible;
}

/* Hide H5 page bottom navigation bar in WeChat environment */
@media screen and (max-width: 768px) {
  html {
    /* Disable long press menu */
    -webkit-touch-callout: none;
    /* Disable text selection */
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    /* Disable double tap zoom */
    -webkit-text-size-adjust: 100%;
    -ms-text-size-adjust: 100%;
  }
  
  body {
    /* Allow normal scrolling */
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: none;
  }
  
  #app {
    /* Allow normal scrolling */
    width: 100%;
    min-height: 100vh;
  }
}

/* Special handling for WeChat browser */
@media screen and (max-width: 768px) {
  /* Detect WeChat environment and apply styles */
  html[data-wechat="true"] {
    /* Force hide browser UI */
    -webkit-appearance: none;
    /* Disable system default touch behavior */
    touch-action: manipulation;
    /* Use CSS custom properties to set height */
    height: calc(var(--vh, 1vh) * 100);
  }
  
  html[data-wechat="true"] body {
    /* Allow normal scrolling in WeChat environment */
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
  
  html[data-wechat="true"] #app {
    /* Allow normal scrolling in WeChat environment */
    overflow: visible;
  }
}

/* Full screen styles in WeChat environment */
html[data-wechat="true"] {
  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

html[data-wechat="true"]::-webkit-scrollbar {
  display: none;
}

/* Global van-button unified styles */
.van-button {
  border-radius: 25px !important;
}

.van-button--primary {
  background: #000000 !important;
  border-color: #000000 !important;
  color: #ffffff !important;
}

.van-button--success {
  background: #000000 !important;
  border-color: #000000 !important;
  color: #ffffff !important;
}

.van-button--warning {
  background: #000000 !important;
  border-color: #000000 !important;
  color: #ffffff !important;
}

.van-button--danger {
  background: #000000 !important;
  border-color: #000000 !important;
  color: #ffffff !important;
}

.van-button--default {
  background: white !important;
  border-color: #ddd !important;
  color: black !important;
}

.van-button--plain {
  background: white !important;
  border-color: #ddd !important;
  color: black !important;
}

/* Global van-dialog footer styles */
:deep(.van-dialog__footer) {
  padding: 8px !important;
  gap: 8px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

/* Stronger selectors to ensure styles take effect */
.van-dialog .van-dialog__footer {
  padding: 12px !important;
  gap: 12px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

/* Global styles, not using scoped */
.van-dialog__footer {
  padding: 12px !important;
  gap: 12px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

.error-boundary {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  text-align: center;
  background-color: #f5f5f5;
}

.error-boundary h3 {
  color: #e74c3c;
  margin-bottom: 16px;
  font-size: 20px;
}

.error-boundary p {
  color: #666;
  margin-bottom: 24px;
  max-width: 400px;
  line-height: 1.5;
}

.reload-btn {
  background-color: #007aff;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.reload-btn:hover {
  background-color: #0056b3;
}

.reload-btn:active {
  background-color: #004494;
}

/* 全局样式：确保Tawk.to聊天工具不遮挡底部导航栏 */
</style>

<style>
/* 超强制全局样式覆盖 - 针对所有可能的Tawk.to元素 */
* {
  /* 覆盖所有可能的Tawk.to相关元素 */
}

/* 使用最高优先级选择器 */
html body * [id*="tawk"],
html body * [class*="tawk"],
html body #tawkchat-minified-container,
html body #tawkchat-container,
html body .tawk-min-container,
html body div[id*="tawk"],
html body div[class*="tawk"],
html body iframe[src*="tawk"] {
  bottom: 90px !important;
  z-index: 999 !important;
}

/* 针对内联样式的强制覆盖 */
html body div[style*="position: fixed"],
html body div[style*="position:fixed"] {
  bottom: 90px !important;
  z-index: 999 !important;
}

/* 针对所有iframe的覆盖 */
html body iframe[src*="tawk.to"],
html body iframe[src*="embed.tawk.to"] {
  z-index: 999 !important;
}

/* 使用属性选择器覆盖所有可能的情况 */
html body [style*="bottom: 20px"],
html body [style*="bottom:20px"],
html body [style*="bottom: 10px"],
html body [style*="bottom:10px"] {
  bottom: 90px !important;
  z-index: 999 !important;
}

/* 移动端响应式调整 */
@media (max-width: 768px) {
  html body * [id*="tawk"],
  html body * [class*="tawk"],
  html body #tawkchat-minified-container,
  html body #tawkchat-container,
  html body .tawk-min-container,
  html body div[id*="tawk"],
  html body div[class*="tawk"],
  html body div[style*="position: fixed"],
  html body div[style*="position:fixed"] {
    bottom: 85px !important;
  }
}

@media (max-width: 480px) {
  html body * [id*="tawk"],
  html body * [class*="tawk"],
  html body #tawkchat-minified-container,
  html body #tawkchat-container,
  html body .tawk-min-container,
  html body div[id*="tawk"],
  html body div[class*="tawk"],
  html body div[style*="position: fixed"],
  html body div[style*="position:fixed"] {
    bottom: 80px !important;
  }
}
</style>

<style>
/* 额外的强制样式 - 使用更高的特异性 */
html body div div div[style*="position"] {
  bottom: 90px !important;
  z-index: 999 !important;
}

/* 针对可能的嵌套结构 */
html body > div[style*="position: fixed"],
html body > div > div[style*="position: fixed"],
html body > div > div > div[style*="position: fixed"] {
  bottom: 90px !important;
  z-index: 999 !important;
}
</style>